/**
 * Screen 4 — Vendor Payment Performance.
 *
 * Built last on purpose: until attribution was separated from the stop clock,
 * this screen would have been a chart of the suspension policy wearing two
 * misleading axis labels. The two axes are rendered side by side and never
 * combined into a single verdict.
 *
 * The test this screen has to pass: a vendor with a CLEAN RECORD must be
 * visibly distinguishable from a vendor whose data failed to load. So there are
 * three states, not two — has disputes, no disputes in period, and no stage
 * history (all invoices imported, so the answer is unknowable). None of them is
 * a 0.0 bar.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, LineChart, ResponsiveContainer } from 'recharts';
import { ChevronRight, Loader2, ShieldCheck, Slash } from 'lucide-react';
import api from '@/lib/api';
import { int, money, num, useIptPrefs, useIptTheme } from './iptTheme';
import { Bar, Def, Empty, Panel, Sample, Tile } from './IptPrimitives';

const STATE_ORDER = { HAS_DISPUTES: 0, NONE_IN_PERIOD: 1, NO_STAGE_HISTORY: 2 };

export const IptVendorPerformance = ({ projectId, isVendor }) => {
  const T = useIptTheme();
  const prefs = useIptPrefs();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('attributed');
  const [ccByVendor, setCcByVendor] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sp = new URLSearchParams();
      if (projectId) sp.set('project_id', projectId);
      sp.set('day_basis', prefs.dayBasis);
      sp.set('stop_clock', String(prefs.stopClock));
      const r = await api.get(`/ipt/metrics/vendors?${sp}`);
      setD(r.data);
    } catch { setD(null); } finally { setLoading(false); }
  }, [projectId, prefs.dayBasis, prefs.stopClock]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const sp = new URLSearchParams();
    if (projectId) sp.set('project_id', projectId);
    api.get(`/ipt/contracts/map?${sp}`)
      .then((r) => setCcByVendor(r.data?.byVendor || {})).catch(() => {});
  }, [projectId]);

  const rows = useMemo(() => {
    const v = [...(d?.vendors || [])];
    const attributed = (x) => (x.attribution?.clientDays || 0)
      + (x.attribution?.vendorDays || 0) + (x.attribution?.neitherDays || 0);
    v.sort((a, b) => {
      // the three states are grouped, so the chart is never a row of empty rails
      const sa = STATE_ORDER[a.disputes?.state] ?? 3;
      const sb = STATE_ORDER[b.disputes?.state] ?? 3;
      if (sa !== sb) return sa - sb;
      if (sortBy === 'value') return (b.value_received || 0) - (a.value_received || 0);
      if (sortBy === 'days') return (b.daysToPayment?.mean || 0) - (a.daysToPayment?.mean || 0);
      return attributed(b) - attributed(a);
    });
    return v.map((x) => ({ ...x, attributedDays: attributed(x) }));
  }, [d, sortBy]);

  if (loading && !d) {
    return (
      <div className="py-20 text-center" data-testid="ipt-vendorperf-loading">
        <Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: T.primary }} />
      </div>
    );
  }
  if (!d) {
    return <Empty T={T} testId="ipt-vendorperf-error">
      Vendor performance could not be loaded. This is a failed load, not a clean record.
    </Empty>;
  }
  if (!rows.length) {
    return (
      <Panel T={T} testId="ipt-vendorperf-empty" title="No vendors with invoices"
        subtitle="An empty register, not a failed load">
        <p className="text-[11.5px] max-w-[620px]" style={{ color: T.body }}>
          No invoices have been raised or imported for this project, so there is nothing
          to score. Switch project in the header.
        </p>
      </Panel>
    );
  }

  const cur = rows[0]?.currency || 'INR';
  const withDisputes = rows.filter((r) => r.disputes?.state === 'HAS_DISPUTES');
  const clean = rows.filter((r) => r.disputes?.state === 'NONE_IN_PERIOD');
  const unknown = rows.filter((r) => r.disputes?.state === 'NO_STAGE_HISTORY');
  const maxAttr = Math.max(...rows.map((r) => r.attributedDays), 1);

  return (
    <div className="space-y-4" data-testid="ipt-vendorperf">
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Tile T={T} testId="vp-tile-vendors" label="Vendors scored" value={int(rows.length)}
          sub={`${int(withDisputes.length)} with disputes · ${int(clean.length)} clean · `
            + `${int(unknown.length)} no history`}
          def={{ title: 'Vendors scored',
            body: 'Every vendor with at least one invoice on this project. A vendor is '
              + 'never dropped for having nothing to report — it is shown with its '
              + 'state named.' }} />
        <Tile T={T} testId="vp-tile-clean" label="Clean records" value={int(clean.length)}
          tone={T.green}
          sub="no disputes in period — stated, not zero-barred"
          def={{ title: 'Clean record',
            body: 'Vendors whose invoices with stage history were never disputed. Shown '
              + 'as an explicit statement because a 0.0 bar reads as missing data, and '
              + 'a clean record is the opposite of missing data.' }} />
        <Tile T={T} testId="vp-tile-unknown" label="No stage history" value={int(unknown.length)}
          tone={unknown.length ? T.amber : undefined}
          sub="all invoices imported — unknowable, not clean"
          def={{ title: 'No stage history',
            body: 'Every invoice for these vendors came from Contracts & Claims without '
              + 'stage history, so whether they disputed cannot be answered from this '
              + 'data. This is missing history and is never counted as clean.' }} />
        <Tile T={T} testId="vp-tile-client" label="Client-side days / invoice"
          value={num(d.comparative ? avg(rows, 'clientSideDays') : rows[0]?.clientSideDays, 1)}
          sub="attribution axis — ours to answer for"
          def={{ title: 'Client-side days', body: d.delayAttributionRule?.note
            || 'Average days per invoice attributed to the client.',
            axis: 'attribution-rule/2' }} />
        <Tile T={T} testId="vp-tile-vendor" label="Vendor-side days / invoice"
          value={num(avg(rows, 'vendorSideDays'), 1)}
          sub="attribution axis — theirs to answer for"
          def={{ title: 'Vendor-side days',
            body: 'Average days per invoice attributed to the vendor. Independent of '
              + 'whether the contract suspended the clock for that period.',
            axis: 'attribution-rule/2' }} />
        <Tile T={T} testId="vp-tile-stopclock" label="Suspended days / invoice"
          value={num(avg(rows, 'stopClockDays'), 1)} tone={T.accent}
          sub="suspension axis — contractual policy"
          def={{ title: 'Suspended days',
            body: 'Average days per invoice spent in a stage the contract suspends. This '
              + 'is policy and says nothing about fault — the two axes disagree on '
              + 'purpose and are never added together.',
            axis: 'suspension-policy/2' }} />
      </div>

      <Sample T={T} testId="vp-axes-sample"
        text={`${d.axes?.suspension?.note || ''} ${d.axes?.attribution?.note || ''} `
          + 'A vendor can have every delay suspended by the contract and still be the '
          + 'party that had to act — suspension and attribution are two readings of the '
          + 'same period, never one combined score.'} />

      <Panel T={T} testId="vp-scorecard"
        title="Vendor scorecard"
        subtitle={`${int(withDisputes.length)} vendors carry disputed time, sorted by `
          + 'attributed days; clean records and vendors without stage history are grouped '
          + 'at the foot'}
        right={(
          <div className="flex items-center gap-1.5">
            {[['attributed', 'attributed days'], ['value', 'value'],
              ['days', 'days to payment']].map(([k, lab]) => (
                <button key={k} type="button" onClick={() => setSortBy(k)}
                  data-testid={`vp-sort-${k}`}
                  className="px-2 py-0.5 rounded text-[10px] font-semibold"
                  style={sortBy === k
                    ? { background: T.tint(T.primary, '1A'), color: T.primary }
                    : { color: T.muted, border: `1px solid ${T.border}` }}>
                  {lab}
                </button>
              ))}
            <Def T={T} testId="vp-scorecard-def" title="Vendor scorecard"
              body={d.delayAttributionRule?.note}
              sample={d.axes?.note || 'Ranking is only shown when every vendor in the set '
                + 'is visible; a vendor reading their own card sees no position.'}
              basis={d.basis} />
          </div>
        )}>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="vp-table">
            <thead>
              <tr style={{ color: T.muted, background: T.surfaceAlt }}>
                {['Vendor', 'Inv', 'Value received', 'Paid', 'Days to pay (gross / net)',
                  'Within terms', 'Disputes', 'Attribution — client / vendor / unknown',
                  'Suspended', 'Retention', 'Trend', ''].map((h, i) => (
                    <th key={h || i}
                      className={`py-2 px-2 font-semibold whitespace-nowrap ${i && i < 10 ? 'text-right' : 'text-left'}`}>
                      {h}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const st = r.disputes?.state;
                const boundary = i > 0 && STATE_ORDER[rows[i - 1].disputes?.state]
                  !== STATE_ORDER[st];
                return (
                  <tr key={r.vendor_id} data-testid={`vp-row-${r.vendor_id}`}
                    style={{ borderTop: boundary ? `2px solid ${T.borderStrong}`
                      : `1px solid ${T.border}`,
                      background: st === 'NO_STAGE_HISTORY' ? T.tint(T.amber, '0A')
                        : 'transparent' }}>
                    <td className="py-1.5 px-2 font-semibold" style={{ color: T.ink }}>
                      {ccByVendor[r.vendor_id] ? (
                        <a href={`/contracts/${ccByVendor[r.vendor_id]}`}
                          data-testid={`vp-cc-link-${r.vendor_id}`} className="hover:underline"
                          style={{ color: T.primary }} title="Open the contract in Contracts & Claims">
                          {r.vendor_name}
                        </a>
                      ) : r.vendor_name}
                      {r.rankByDaysToPayment && (
                        <span className="ml-1.5 text-[9px]" style={{ color: T.muted }}>
                          #{int(r.rankByDaysToPayment)} of {int(r.rankOf)}
                        </span>
                      )}
                    </td>
                    <Num T={T} v={int(r.invoices_received)} />
                    <Num T={T} v={money(r.value_received, r.currency || cur)} bold />
                    <Num T={T} v={`${int(r.invoices_paid)} · ${money(r.value_paid, r.currency || cur)}`} />
                    <Num T={T} v={r.daysToPayment?.mean === null || r.daysToPayment?.mean === undefined
                      ? '—'
                      : `${num(r.daysToPayment.mean, 1)} / ${num(r.daysToPaymentNet?.mean, 1)}`} />
                    <Num T={T} v={r.pct_paid_within_terms === null ? '—'
                      : `${num(r.pct_paid_within_terms, 0)}%`}
                      tone={r.pct_paid_within_terms !== null && r.pct_paid_within_terms < 80
                        ? T.red : undefined} />
                    <td className="py-1.5 px-2 text-right whitespace-nowrap">
                      <DisputeCell r={r} T={T} />
                    </td>
                    <td className="py-1.5 px-2" style={{ minWidth: 190 }}>
                      {st === 'NO_STAGE_HISTORY' ? (
                        <span className="text-[10px]" style={{ color: T.amber }}>
                          no stage history to attribute
                        </span>
                      ) : (
                        <>
                          <div className="flex items-baseline gap-2 tabular-nums text-[10.5px]">
                            <span style={{ color: T.primary }}>
                              {num(r.attribution?.clientDays, 1)}
                            </span>
                            <span style={{ color: T.amber }}>
                              {num(r.attribution?.vendorDays, 1)}
                            </span>
                            <span style={{ color: T.muted }}>
                              {num(r.attribution?.neitherDays, 1)}
                            </span>
                            <span className="ml-auto text-[9px]" style={{ color: T.muted }}>
                              {r.delayDrivenBy === 'n/a' ? '' : `${r.delayDrivenBy}-driven`}
                            </span>
                          </div>
                          <Bar T={T} height={6} segments={[
                            { label: 'client', value: r.attribution?.clientDays,
                              colour: T.primary },
                            { label: 'vendor', value: r.attribution?.vendorDays,
                              colour: T.amber },
                            { label: 'unknown', value: r.attribution?.neitherDays,
                              colour: T.muted },
                          ]} />
                          <div className="h-[3px] mt-0.5 rounded-full"
                            style={{ width: `${100 * r.attributedDays / maxAttr}%`,
                                     background: T.tint(T.primary, '55') }} />
                        </>
                      )}
                    </td>
                    <Num T={T} v={r.stopClock?.days === null || r.stopClock?.days === undefined
                      ? '—' : `${num(r.stopClock.days, 1)} d`}
                      tone={r.stopClock?.days > 0 ? T.accent : T.muted} />
                    <Num T={T} v={r.retention_held ? money(r.retention_held, r.currency || cur)
                      : '—'} />
                    <td className="py-1.5 px-2" style={{ width: 90 }}>
                      {(r.sparkline || []).length > 1 ? (
                        <div className="h-6" data-testid={`vp-spark-${r.vendor_id}`}>
                          <ResponsiveContainer width="100%" height="100%" minWidth={0}
                            minHeight={10} debounce={60}>
                            <LineChart data={r.sparkline}>
                              <Line type="monotone" dataKey="avgDays" stroke={T.primary}
                                strokeWidth={1.5} dot={false} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      ) : <span className="text-[9.5px]" style={{ color: T.muted }}>
                        too few months
                      </span>}
                    </td>
                    <td className="py-1.5 px-2 text-right">
                      <button type="button" data-testid={`vp-drill-${r.vendor_id}`}
                        onClick={() => navigate('/project-controls/cost/invoices'
                          + `?tab=register&vendor_id=${r.vendor_id}`)}
                        className="inline-flex items-center text-[9.5px] underline"
                        style={{ color: T.primary }}>
                        invoices <ChevronRight className="w-3 h-3" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!!clean.length && (
          <p className="mt-2 text-[10px]" data-testid="vp-clean-record-note" style={{ color: T.muted }}>
            {int(clean.length)} vendor(s) below the first divider finished the period with
            no disputes at all. That is a clean record, not an empty one — it is written
            out rather than drawn as a zero so it can never be mistaken for data that
            failed to load.
          </p>
        )}
        {!!unknown.length && (
          <p className="mt-1 text-[10px]" data-testid="vp-nohistory-group-note"
            style={{ color: T.amber }}>
            {int(unknown.length)} vendor(s) below the second divider have only imported
            invoices. Whether they disputed is not knowable from this data, so no dispute
            figure is shown for them at all.
          </p>
        )}
        <Sample T={T} testId="vp-sample"
          text={rows[0]?.sample?.statement
            ? `Example — ${rows[0].vendor_name}: ${rows[0].sample.statement}`
            : ''} />
      </Panel>
    </div>
  );
};

const DisputeCell = ({ r, T }) => {
  const st = r.disputes?.state;
  if (st === 'NONE_IN_PERIOD') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold"
        data-testid={`vp-clean-${r.vendor_id}`} title={r.disputes.statement}
        style={{ color: T.green }}>
        <ShieldCheck className="w-3 h-3" /> no disputes in period
      </span>
    );
  }
  if (st === 'NO_STAGE_HISTORY') {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-semibold"
        data-testid={`vp-nohistory-${r.vendor_id}`} title={r.disputes.statement}
        style={{ color: T.amber }}>
        <Slash className="w-3 h-3" /> no stage history
      </span>
    );
  }
  return (
    <span className="tabular-nums text-[10.5px]" data-testid={`vp-disputes-${r.vendor_id}`}
      title={r.disputes?.statement} style={{ color: T.ink }}>
      {int(r.disputes?.everCount)} ever
      <span className="ml-1" style={{ color: r.disputes?.openCount ? T.red : T.muted }}>
        · {int(r.disputes?.openCount)} open
      </span>
      <span className="block text-[9px]" style={{ color: T.muted }}>
        {num(r.disputes?.rate, 0)}% · avg {num(r.disputes?.daysInDispute, 0)} d
      </span>
    </span>
  );
};

const Num = ({ v, T, tone, bold }) => (
  <td className={`py-1.5 px-2 text-right tabular-nums whitespace-nowrap ${bold ? 'font-semibold' : ''}`}
    style={{ color: tone || T.body }}>{v}</td>
);

const avg = (rows, key) => {
  const v = rows.map((r) => r[key]).filter((x) => x !== null && x !== undefined);
  return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null;
};

export default IptVendorPerformance;
